import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, BadgeCheck, MapPin, Plus, Search, Star } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SERVICE_CATALOG } from '@/lib/domain';
import { deburr } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { Avatar } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './Home.module.css';

const VALUE_PROPS = [
  { id: '1', title: 'Prestataires vérifiés', image: '/assets/illustrations/verified.png' },
  { id: '2', title: 'Service de qualité', image: '/assets/illustrations/star.png' },
  { id: '3', title: 'Rapide & fiable', image: '/assets/illustrations/sparkles.png' },
  { id: '4', title: 'Support à votre écoute', image: '/assets/illustrations/support.png' },
];

type FeaturedProvider = {
  id: string;
  rating: number;
  description: string | null;
  full_name: string;
  city: string | null;
};

export default function ClientHome() {
  const navigate = useNavigate();
  const userId = useUserId();
  const [firstName, setFirstName] = useState('');
  const [search, setSearch] = useState('');
  const [providers, setProviders] = useState<FeaturedProvider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);

  useEffect(() => {
    if (!userId) return;
    void (async () => {
      const { data } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      if (data?.full_name) setFirstName(data.full_name.split(' ')[0]);
    })();
  }, [userId]);

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('providers')
        .select('id, rating, description, profiles(full_name, city)')
        .eq('is_approved', true)
        .order('rating', { ascending: false })
        .limit(3);

      setProviders(
        (data ?? []).map((row: any) => ({
          id: row.id,
          rating: Number(row.rating ?? 0),
          description: row.description,
          full_name: row.profiles?.full_name ?? 'Prestataire',
          city: row.profiles?.city ?? null,
        }))
      );
      setLoadingProviders(false);
    })();
  }, []);

  const matchingServices = useMemo(() => {
    const query = deburr(search.trim());
    if (!query) return SERVICE_CATALOG;
    return SERVICE_CATALOG.filter((service) => deburr(service.name).includes(query));
  }, [search]);

  const goToService = (service: (typeof SERVICE_CATALOG)[number]) => {
    navigate(`/app/providers?service=${encodeURIComponent(service.name)}`);
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <p className={styles.greeting}>Bonjour{firstName ? `, ${firstName}` : ' !'}</p>
          <p className={styles.subGreeting}>Comment pouvons-nous vous aider ?</p>

          <div className={styles.search}>
            <Search size={21} color="var(--ink-3)" aria-hidden="true" />
            <input
              className={styles.searchInput}
              type="search"
              placeholder="Rechercher un service..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Rechercher un service"
            />
          </div>
        </div>
      </section>

      <div className={styles.body}>
        <section className={styles.cta}>
          <div className={styles.ctaText}>
            <h2 className={styles.ctaTitle}>Besoin d&apos;un service ?</h2>
            <p className={styles.ctaSubtitle}>Créez une demande en quelques clics.</p>
            <Link className="btn btn--primary" to="/app/new-request">
              <Plus size={20} /> Créer une demande
            </Link>
          </div>
          <img className={styles.ctaImage} src="/assets/illustrations/house.png" alt="" />
        </section>

        <section>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Nos services</h2>
            <Link className={styles.sectionLink} to="/app/providers">
              Voir tout
            </Link>
          </header>

          {matchingServices.length === 0 ? (
            <p className={styles.noResults}>Aucun service ne correspond à « {search} ».</p>
          ) : (
            <ul className={styles.serviceGrid}>
              {matchingServices.map((service) => (
                <li key={service.id}>
                  <button
                    type="button"
                    className={styles.serviceCard}
                    onClick={() => goToService(service)}
                  >
                    <span className={styles.serviceArt} style={{ background: service.tint }}>
                      <img className={styles.serviceIcon} src={service.image} alt="" />
                    </span>
                    <span className={styles.serviceName}>
                      {service.name}
                      <ArrowRight size={16} className={styles.serviceArrow} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.values}>
          {VALUE_PROPS.map((item, index) => {
            const isSupport = index === VALUE_PROPS.length - 1;
            const content = (
              <>
                <img className={styles.valueIcon} src={item.image} alt="" />
                <span className={styles.valueText}>{item.title}</span>
              </>
            );
            return isSupport ? (
              <Link key={item.id} className={styles.valueItem} to="/app/profile?support=1">
                {content}
              </Link>
            ) : (
              <div key={item.id} className={styles.valueItem}>
                {content}
              </div>
            );
          })}
        </section>

        <section>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Prestataires en vedette</h2>
            <Link className={styles.sectionLink} to="/app/providers">
              Voir tout
            </Link>
          </header>

          {loadingProviders ? (
            <Spinner block />
          ) : providers.length === 0 ? (
            <p className={styles.noResults}>Aucun prestataire disponible pour l&apos;instant.</p>
          ) : (
            <ul className={styles.providerList}>
              {providers.map((provider) => (
                <li key={provider.id} className={styles.providerCard}>
                  <Avatar name={provider.full_name} size={58} tone="orange" />
                  <div className={styles.providerInfo}>
                    <p className={styles.providerName}>
                      {provider.full_name}
                      <BadgeCheck size={16} className={styles.verified} aria-label="Vérifié" />
                    </p>
                    {provider.description ? (
                      <p className={styles.providerDescription}>{provider.description}</p>
                    ) : null}
                    <p className={styles.providerMeta}>
                      {provider.rating > 0 ? (
                        <span className={styles.providerRating}>
                          <Star size={13} color="#FFB21A" fill="#FFB21A" />
                          {provider.rating.toFixed(1)}
                        </span>
                      ) : null}
                      <span className={styles.providerCity}>
                        <MapPin size={13} />
                        {provider.city || 'Disponible près de chez vous'}
                      </span>
                    </p>
                  </div>
                  <Link className="btn btn--ghost btn--sm" to={`/app/providers/${provider.id}`}>
                    Voir
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
